/*

Usage <SimpleDonut a={oneValue} b={anotherValue} className="aClass" unit="%"/>

a: mandatory left hand value
b: mandatory right hand value
className: optionally set the base class for all the element. All sub classes will use this base.
unit: optional unit to display in the middle of the donut

*/
import PropTypes from 'prop-types';

import React from 'react';

export default function SimpleDonut({
  a,
  b,
  unit = "",
  className = "simple-donut"
}) {
  // these two values chosen to match dimensions given in design
  const outerRadius = 80;
  const donutThickness = 20;

  // derive some dimensions from them
  const svgSize = outerRadius * 2;
  const innerRadius = outerRadius - donutThickness;

  // trigonometry for calculating the end point
  let angle;

  if (a + b == 0) {
    angle = Math.PI;
  }
  else {
    angle = Math.PI * 2 * b / (a + b);
  }

  const sin = Math.sin(angle);
  const cos = Math.cos(angle);

  // the fout apex points required for the path
  const startPoint = `${outerRadius} 0`;
  const innerStartPoint = `${outerRadius} ${donutThickness}`;

  const outerEndPoint = getPoint(outerRadius, sin, cos);
  const innerEndPoint = getPoint(innerRadius, sin, cos);

  // Required for arcs that are larger than a half turn
  const outerBottomPoint = getPoint(outerRadius, 0, -1); // ie angle is pi
  const innerBottomPoint = getPoint(innerRadius, 0, -1); // ie angle is pi

  const leftArcIsLarge = (a > b);

  // some helpers that clarify the calculations
  // inner functions to keep their signature clean

  function getPoint(radius, sin, cos) {
    return `${outerRadius + radius * sin} ${outerRadius - radius * cos}`;
  }

  function getArc(radius, sweepFlag, endPoint) {
    // arc is circular, so both radii are the same and that axes aren't rotated
    // arc sweeps no more than half a turn (it gets split in two if needed),
    // so the large arc flag is always zero
    return `A${radius} ${radius} 0 0 ${sweepFlag} ${endPoint}`;
  }

  function getPath(arcIsLarge, arcIsLeft) {
    // sweep one way there and the other back
    // left and right sweep opposite ways
    const innerSweepFlag = arcIsLeft * 1;
    const outerSweepFlag = 1 - innerSweepFlag;

    let outerArc = getArc(outerRadius, outerSweepFlag, outerEndPoint);
    let innerArc = getArc(innerRadius, innerSweepFlag, innerStartPoint);

    // split the arc in two if it sweeps more than half a turn, by adding half turn arc first
    // * solves the full circle problem
    // * removes the need to calculate the large arc flag
    if (arcIsLarge) {
      outerArc = `${getArc(outerRadius, outerSweepFlag, outerBottomPoint)}${outerArc}`
      innerArc = `${getArc(innerRadius, innerSweepFlag, innerBottomPoint)}${innerArc}`
    }

    // move the top, arc around the outside, line to the inside, arc back along the inside, and close the path
    return `M${startPoint}${outerArc}L${innerEndPoint}${innerArc}z`;
  }

  return (
    <div className={className}>
      <svg className={`${className}-donut`} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${svgSize} ${svgSize}`}>
        <path className={`${className}-arc-a`} d={getPath(leftArcIsLarge, true)}/>
        <path className={`${className}-arc-b`} d={getPath(!leftArcIsLarge, false)}/>
      </svg>
      <span className={`${className}-text-a`}>{a}</span>
      <span className={`${className}-text-b`}>{b}</span>
      <span className={`${className}-text-unit`}>{unit}</span>
    </div>
  );
}

SimpleDonut.propTypes = {
  className: PropTypes.string,
  unit: PropTypes.string,
  a: PropTypes.number.isRequired,
  b: PropTypes.number.isRequired
}




// WEBPACK FOOTER //
// ./modules/Common/components/svg/SimpleDonut.js